import { updateUserProfileDetails } from '@/api/backend/profile';
import { CompanyProfileType, InvestorProfileType } from '@/api/backend/types';
import { COUNTRY_MOBILE_CODES } from '@/lib/constants';
import { getCountryCodeFromFullNumber, getLocalNumberFromFullNumber } from '@/lib/utils';
import { Validators } from '@/lib/validator';
import { queryClient } from '@/services/queryClient';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Flag,
  FlagCountryCode,
  FormError,
  FormField,
  FormLabel,
  FormLabelGroup,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@opaz/ui-components';
import { useMutation } from '@tanstack/react-query';
import { CountryCode, getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { useMemo } from 'react';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

type NotificationSettingsInput = {
  countryIso2: FlagCountryCode;
  localNumber: string | null;
  email: string;
};

const phoneWithCountryValidator = Validators.validatePhoneWithCountry('countryIso2', 'localNumber');

const mobileFormSchema = z
  .object({
    email: z.string().email().min(1).max(100),
    countryIso2: z.string() as z.ZodType<FlagCountryCode>,
    localNumber: z
      .string()
      .min(1)
      .max(20)
      .refine(...Validators.stringIsNumber)
      .nullable(),
  })
  .refine(phoneWithCountryValidator.validator, {
    params: phoneWithCountryValidator.params,
    path: phoneWithCountryValidator.path,
  });

type NotificationSettingsProps = { profile: InvestorProfileType | CompanyProfileType };

export const NotificationSettings = ({ profile }: NotificationSettingsProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { mutateAsync: updateNotificationSettings, isPending: isSaveLoading } = useMutation({
    mutationFn: async (data: { phoneNumber: string; emailAddress: string }) => {
      await updateUserProfileDetails({
        phoneNumber: data.phoneNumber,
        emailAddress: data.emailAddress,
        languagePreference: profile.languagePreference,
      });
    },
    onSuccess: () => {
      toast({
        message: t('profile.notificationSettings.editSuccessMessage'),
        variant: 'success',
      });
    },
  });

  const {
    control,
    register,
    formState: { errors, isDirty: isFormDirty },
    handleSubmit,
    reset,
  } = useForm<NotificationSettingsInput>({
    disabled: isSaveLoading,
    defaultValues: {
      countryIso2: getCountryCodeFromFullNumber(profile.notificationDetails.mobileNumber) ?? 'OM',
      localNumber: getLocalNumberFromFullNumber(profile.notificationDetails.mobileNumber),
      email: profile.notificationDetails.email,
    },
    resolver: zodResolver(mobileFormSchema),
  });

  const onSubmit: SubmitHandler<NotificationSettingsInput> = async (data) => {
    await updateNotificationSettings({
      phoneNumber: `${COUNTRY_MOBILE_CODES.find((code) => code.countryIso2 === data.countryIso2)?.mobileCode}${data.localNumber}`,
      emailAddress: data.email,
    });

    reset({
      countryIso2: data.countryIso2,
      localNumber: data.localNumber,
      email: data.email,
    });

    queryClient.invalidateQueries({
      queryKey: ['userProfileDetails', profile.id],
    });
  };

  const { field: countryIso2Field } = useController({
    control,
    name: 'countryIso2',
  });

  const flagOptions = useMemo(() => {
    return COUNTRY_MOBILE_CODES.map((country) => {
      return (
        <SelectItem value={country.countryIso2} key={country.countryIso2}>
          <div className="flex items-center gap-2">
            <Flag countryCode={country.countryIso2} />
            {country.mobileCode}
          </div>
        </SelectItem>
      );
    });
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h1 className="text-foreground text-3xl font-bold mb-4">{t('profile.notificationSettings.title')}</h1>
      <p>{t('profile.notificationSettings.description')}</p>

      <div className="flex flex-col gap-5 max-w-[632px] my-12">
        <FormField>
          <FormLabelGroup>
            <FormLabel error={!!errors.localNumber}>{t('profile.notificationSettings.fields.mobile')}</FormLabel>

            {errors.localNumber && (
              <FormError>{t(errors.localNumber.message as unknown as TemplateStringsArray)}</FormError>
            )}
          </FormLabelGroup>

          <div className="flex gap-3">
            <Select
              value={countryIso2Field.value}
              onValueChange={countryIso2Field.onChange}
              disabled={countryIso2Field.disabled}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{flagOptions}</SelectContent>
            </Select>

            <div className="w-full">
              <Input
                className="w-full"
                error={!!errors.localNumber}
                {...register('localNumber')}
                placeholder={getExampleNumber(countryIso2Field.value as CountryCode, examples)?.nationalNumber}
              />
            </div>
          </div>
        </FormField>

        <FormField>
          <FormLabelGroup>
            <FormLabel error={!!errors.email}> {t('profile.notificationSettings.fields.email')}</FormLabel>

            {errors.email && <FormError>{t(errors.email.message as unknown as TemplateStringsArray)}</FormError>}
          </FormLabelGroup>

          <Input error={!!errors.email} {...register('email')} />
        </FormField>
      </div>

      {isFormDirty && (
        <div className="flex gap-3">
          <Button type="submit" disabled={isSaveLoading}>
            {t('common.save')}
          </Button>

          <Button variant="secondary" type="button" onClick={() => reset()} disabled={isSaveLoading}>
            {t('common.cancel')}
          </Button>
        </div>
      )}
    </form>
  );
};
